import * as React from 'react'

import { Box, Button, SelectOptionGroups } from '@factorialco/deprecated-design-system'
import { HiringStagePhaseFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { HiringStage } from 'modules/ats/hooks/hiringStages/useHiringStages'
import { useUpdateAtsHiringPhase } from 'modules/ats/hooks/hiringStages/useUpdateAtsHiringPhase'

interface HiringPhaseFormValues {
  id: string
  phaseName: string
  atsHiringStageId: string
}

interface Props {
  onClose: () => void
  atsHiringPhase: HiringStagePhaseFragment
  stages: HiringStage[]
}

export const EditHiringPhaseModal = ({ onClose, atsHiringPhase, stages }: Props) => {
  const { updateAtsHiringPhase, loading } = useUpdateAtsHiringPhase()
  const stageOptions: SelectOptionGroups = stages.map((item) => {
    return { label: item.label, value: item.id }
  })
  const form = useForm<HiringPhaseFormValues>({
    defaultValues: {
      id: atsHiringPhase.id,
      phaseName: atsHiringPhase.name,
      atsHiringStageId: atsHiringPhase.atsHiringStageId,
    },
    validate: (data) => {
      const errors: FormErrors<HiringPhaseFormValues> = {}

      if (!data.phaseName) {
        errors.phaseName = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateAtsHiringPhase(
        atsHiringPhase.id,
        data.phaseName,
        String(data.atsHiringStageId)
      )
      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('ats.hiring_pipelines.settings.sidepanel.edit_phase_modal.title')}
      illustration="automations"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} type="negative" submit>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <TextField
          label={i18n.t('ats.hiring_pipelines.settings.sidepanel.edit_phase_modal.phase_label')}
          value={form.values.phaseName}
          onChange={(value) => form.setValues({ ...form.values, phaseName: value })}
          error={form.touched.phaseName ? form.errors.phaseName : undefined}
          mandatory
        />
        <Select
          none={i18n.t(
            'ats.hiring_pipelines.settings.sidepanel.edit_phase_modal.stage_placeholder'
          )}
          options={stageOptions}
          value={String(form.values.atsHiringStageId)}
          onChange={(value) => form.setFieldValue('atsHiringStageId', value)}
          mandatory
        />
      </Box>
    </Modal>
  )
}
